/**
 * Knex implementation of ICollectionRepository.
 *
 * Pure Knex adapter — no service deps, no hooks, no JSON parsing.
 * Services handle hooks, JSON parsing, and business rules on top.
 */

import type { Knex } from 'knex'
import type { EntityQuery, FieldDefinition, PaginatedResult } from '@gzl10/nexus-sdk'
import type { ICollectionRepository, RepoFilters } from '../types.js'
import {
  applyFilters,
  applySorting,
  applyPagination,
  applySearchOnFields,
  type SortOptions
} from './helpers.js'
import { handleDbError } from '../../../db/knex-adapter.js'

export interface KnexCollectionRepoOptions {
  searchableFields?: string[]
  defaultSort?: { field: string; order: 'asc' | 'desc' } | null
  labelField?: string
  fieldDefs?: Record<string, FieldDefinition>
  /** Maximum page size cap. Defaults to 100. */
  maxLimit?: number
}

export class KnexCollectionRepository<T extends Record<string, unknown> = Record<string, unknown>>
  implements ICollectionRepository<T>
{
  constructor(
    protected readonly knex: Knex,
    protected readonly table: string,
    protected readonly options: KnexCollectionRepoOptions = {}
  ) {}

  async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    const page = Math.max(1, query?.page ?? 1)
    const maxLimit = this.options.maxLimit ?? 100
    const limit = query?.limit === 0 ? 0 : Math.min(maxLimit, Math.max(1, query?.limit ?? 20))
    const offset = limit === 0 ? 0 : (page - 1) * limit

    let qb: Knex.QueryBuilder = this.knex(this.table)

    // Apply search
    if (query?.search) {
      const { searchableFields, labelField, fieldDefs } = this.options
      const fields = [
        ...(searchableFields ?? []),
        ...(labelField && !searchableFields?.includes(labelField) ? [labelField] : [])
      ]
      if (fields.length > 0) {
        qb = applySearchOnFields(qb, query.search, fields, fieldDefs)
      }
    }

    // Apply filters
    if (query?.filters) {
      qb = applyFilters(qb.clone(), query.filters)
    }

    // Count before adding sorting/pagination
    const total = Number(
      (await qb.clone().count('* as count').first<{ count: string | number }>())?.count ?? 0
    )

    // Sort options mirror SortOptions interface
    const sortOpts: SortOptions = {
      defaultSort: this.options.defaultSort ?? undefined,
      labelField: this.options.labelField
    }

    applySorting(qb, query, sortOpts)
    applyPagination(qb, limit, offset)

    const items = (await qb) as T[]

    const totalPages = limit === 0 ? 1 : Math.ceil(total / limit)

    return {
      items,
      total,
      page: limit === 0 ? 1 : page,
      limit: limit === 0 ? total : limit,
      totalPages,
      hasNext: limit === 0 ? false : page < totalPages
    }
  }

  async findById(id: string): Promise<T | null> {
    const row = await this.knex(this.table).where('id', id).first<T | undefined>()
    return row ?? null
  }

  async create(data: Partial<T>): Promise<T> {
    try {
      const inserted = await this.knex(this.table).insert(data).returning('*')
      return (inserted[0] ?? data) as T
    } catch (e) {
      handleDbError(e)
    }
  }

  async update(id: string, data: Partial<T>): Promise<T> {
    try {
      const updated = await this.knex(this.table).where('id', id).update(data).returning('*')
      return (updated[0] ?? (await this.findById(id))) as T
    } catch (e) {
      handleDbError(e)
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await this.knex(this.table).where('id', id).delete()
    } catch (e) {
      handleDbError(e)
    }
  }

  async count(filters?: RepoFilters): Promise<number> {
    let qb: Knex.QueryBuilder = this.knex(this.table)
    if (filters) {
      qb = applyFilters(qb, filters)
    }
    const result = await qb.count('* as count').first<{ count: string | number }>()
    return Number(result?.count ?? 0)
  }

  async bulkCreate(items: Partial<T>[]): Promise<T[]> {
    if (items.length === 0) return []
    try {
      const created = await this.knex(this.table).insert(items).returning('*')
      return created as T[]
    } catch (e) {
      handleDbError(e)
    }
  }

  async bulkUpdate(items: Array<{ id: string; data: Partial<T> }>): Promise<T[]> {
    if (items.length === 0) return []
    return this.knex.transaction(async (trx) => {
      const results: T[] = []
      for (const { id, data } of items) {
        const updated = await trx(this.table).where('id', id).update(data).returning('*')
        results.push((updated[0] ?? await trx(this.table).where('id', id).first()) as T)
      }
      return results
    })
  }

  async bulkDelete(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    try {
      await this.knex(this.table).whereIn('id', ids).delete()
    } catch (e) {
      handleDbError(e)
    }
  }
}
